import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  Table,
  Card,
  Button,
  Spin,
  message,
  Input,
  Select,
  DatePicker,
  Space,
  Checkbox,
  Tooltip
} from 'antd'
import {
  LogoutOutlined,
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  DollarOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../utils/api'
import Hint from '../components/common/Hint'
import { formatTz, now as tzNow } from '../utils/timezone'

const { RangePicker } = DatePicker

/** 與後端一致：每月 12 日起視為可結「截止月為上月」之期；12 日前預設選上個截止月 */
function defaultSettlementClosingYm() {
  const n = tzNow()
  if (n.date() >= 12) return n.format('YYYY-MM')
  return n.subtract(1, 'month').format('YYYY-MM')
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f5f0 0%, #e8e8e8 50%, #f0ede5 100%);
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px 10px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const Title = styled.h1`
  font-size: 28px;
  color: #5a5a5a;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`

const StyledButton = styled(Button)`
  @media (max-width: 768px) {
    width: 100%;
  }
`

const FilterCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  background: #faf9f6;
  border: 1px solid rgba(168, 181, 192, 0.2);

  .ant-card-body {
    padding: 20px;
  }
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const FilterItem = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
  }
`

const SummaryCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  background: linear-gradient(135deg, #faf9f6 0%, #f5f5f0 100%);
  border: 1px solid rgba(168, 181, 192, 0.3);

  .ant-card-head {
    border-bottom: 1.5px solid rgba(168, 181, 192, 0.3);
    background: transparent;
  }

  .ant-card-head-title {
    font-size: 20px;
    font-weight: 500;
    color: #5a5a5a;
    letter-spacing: 0.3px;
  }
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(168, 181, 192, 0.2);
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(154, 163, 175, 0.2);
  }
`

const SummaryLabel = styled.span`
  font-size: 13px;
  color: #8a8a8a;
  margin-bottom: 8px;
  font-weight: 400;
  letter-spacing: 0.5px;
`

const SummaryValue = styled.span`
  font-size: 24px;
  color: #5a5a5a;
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

const SummaryValueLarge = styled.span`
  font-size: 24px;
  color: #5a5a5a;
  font-weight: 500;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`

// 自定义 Tag 组件 - 莫兰迪色系
const CustomTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
  border: 1px solid;

  ${(props) => {
    switch (props.variant) {
      case 'success':
        return `
          background: #e8f5e9;
          color: #5a8a5a;
          border-color: #c4d4c4;
        `
      case 'error':
        return `
          background: #fce4ec;
          color: #b85a6a;
          border-color: #e4c4d4;
        `
      case 'warning':
        return `
          background: #fff3e0;
          color: #b8956a;
          border-color: #e4d4c4;
        `
      case 'info':
        return `
          background: #e3f2fd;
          color: #5a7a9a;
          border-color: #c4d4e4;
        `
      case 'blue':
        return `
          background: #e8eef5;
          color: #6a8a9a;
          border-color: #c4d4e4;
        `
      default:
        return `
          background: #f5f5f0;
          color: #8a8a8a;
          border-color: #d4d4c4;
        `
    }
  }}

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 4px 8px;
    gap: 4px;
    border-radius: 6px;
    border: 1px solid;
  }
`

const StyledTable = styled(Table)`
  background: #faf9f6;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);

  .ant-table {
    background: transparent;
  }

  .ant-table-thead > tr > th {
    background: #ffffff;
    border-bottom: 1px solid rgba(168, 181, 192, 0.2);
    color: #5a5a5a;
    font-weight: 500;
  }

  .ant-table-tbody > tr {
    background: #ffffff;
    transition: all 0.2s ease;

    &:hover {
      background: #f8f7f4;
      cursor: pointer;
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid rgba(168, 181, 192, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

const SubscriptionListPage = () => {
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [token, setToken] = useState(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    status: undefined,
    startDate: undefined,
    endDate: undefined,
    search: ''
  })
  /** 預設不顯示未付款；勾選後與後端 excludePending 解除搭配列出 pending */
  const [includePendingInList, setIncludePendingInList] = useState(false)
  const [summary, setSummary] = useState(null)
  const [calculationMode, setCalculationMode] = useState('monthly') // 'monthly' 或 'yearly'
  const [excludeYearlyFromCommission, setExcludeYearlyFromCommission] =
    useState(false) // 分潤計算是否排除年繳
  /** 結算期以「截止月」YYYY-MM 表示（該月 11 日止）；預設對齊每月 11 日界 */
  const [settlementMonth, setSettlementMonth] = useState(
    defaultSettlementClosingYm
  )
  const navigate = useNavigate()

  useEffect(() => {
    const savedToken = sessionStorage.getItem('subscription_view_token')
    if (!savedToken) {
      message.warning('請先登入')
      navigate('/subscription/login')
      return
    }
    setToken(savedToken)
    fetchSubscriptions()
  }, [navigate])

  useEffect(() => {
    if (token) {
      fetchSubscriptions()
    }
  }, [
    pagination.current,
    pagination.pageSize,
    filters,
    excludeYearlyFromCommission,
    settlementMonth,
    includePendingInList
  ])

  const fetchSubscriptions = async () => {
    const currentToken =
      token || sessionStorage.getItem('subscription_view_token')
    if (!currentToken) {
      navigate('/subscription/login')
      return
    }

    try {
      setLoading(true)
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize
      }

      if (filters.status) params.status = filters.status
      else if (!includePendingInList) params.excludePending = true
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate
      if (excludeYearlyFromCommission)
        params.excludeYearlyFromCommission = true
      if (settlementMonth && /^\d{4}-\d{2}$/.test(settlementMonth)) {
        params.settlementMonth = settlementMonth
      }

      const response = await api.get('/api/subscription/all', {
        headers: {
          Authorization: `Bearer ${currentToken}`
        },
        params
      })

      if (response.data.success) {
        const data = response.data.data
        setSubscriptions(data.subscriptions || [])
        setSummary(data.summary || null)
        setPagination((prev) => ({
          ...prev,
          total: data.pagination?.total || 0
        }))
      } else {
        message.error(response.data.message || '獲取訂閱數據失敗')
      }
    } catch (error) {
      console.error('獲取訂閱數據錯誤:', error)

      if (error.response?.status === 401) {
        const errorMessage = error.response?.data?.message || ''
        if (errorMessage.includes('過期') || errorMessage.includes('無效')) {
          sessionStorage.removeItem('subscription_view_token')
          setToken(null)
          message.error('登入已過期，請重新登入')
          navigate('/subscription/login')
          return
        }
      }

      message.error(
        error.response?.data?.message || '獲取訂閱數據失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-'
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateValue) => {
    if (!dateValue) return '-'

    let date
    if (typeof dateValue === 'object' && dateValue !== null) {
      if (dateValue.$date) {
        date = new Date(dateValue.$date)
      } else if (dateValue instanceof Date) {
        date = dateValue
      } else {
        return '-'
      }
    } else if (typeof dateValue === 'string') {
      if (dateValue.length === 8 && /^\d+$/.test(dateValue)) {
        return `${dateValue.substring(0, 4)}-${dateValue.substring(
          4,
          6
        )}-${dateValue.substring(6, 8)}`
      }
      date = new Date(dateValue)
    } else {
      return '-'
    }

    if (isNaN(date.getTime())) {
      return dateValue.toString()
    }

    // toLocaleString('zh-TW') 只設語系、不設時區
    return formatTz(date, 'YYYY/MM/DD HH:mm')
  }

  const getStatusTag = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: '啟用中' },
      pending: { variant: 'default', text: '未付款' },
      paused: { variant: 'error', text: '已暫停' },
      expired: { variant: 'warning', text: '已過期' }
    }
    const config = statusConfig[status] || { variant: 'default', text: status }
    return <CustomTag variant={config.variant}>{config.text}</CustomTag>
  }

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    })
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }))
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters((prev) => ({
        ...prev,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD')
      }))
    } else {
      setFilters((prev) => ({
        ...prev,
        startDate: undefined,
        endDate: undefined
      }))
    }
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const handleResetFilters = () => {
    setFilters({
      status: undefined,
      startDate: undefined,
      endDate: undefined,
      search: ''
    })
    setIncludePendingInList(false)
    setSettlementMonth(defaultSettlementClosingYm())
    setPagination((prev) => ({ ...prev, current: 1 }))
  }

  const settlementColumns = useMemo(
    () => [
      {
        title: '訂單號',
        dataIndex: 'merchantOrderNo',
        key: 'merchantOrderNo',
        width: 200,
        ellipsis: true
      },
      {
        title: '客戶',
        dataIndex: 'customerName',
        key: 'customerName',
        width: 120,
        ellipsis: true
      },
      {
        title: '方案類型',
        dataIndex: 'paymentType',
        key: 'paymentType',
        width: 88,
        render: (t) =>
          ({ monthly: '月繳', yearly: '年繳', one_time: '單次' }[t] || t || '-')
      },
      {
        title: '狀態',
        dataIndex: 'status',
        key: 'status',
        width: 88,
        render: (s) =>
          ({
            active: '啟用中',
            pending: '未付款',
            paused: '已暫停',
            expired: '已過期',
            terminated: '已終止'
          }[s] || s)
      },
      {
        title: '計入方式',
        dataIndex: 'basisLabel',
        key: 'basisLabel',
        ellipsis: true
      },
      {
        title: '扣款日（歸屬）',
        dataIndex: 'deductionDates',
        key: 'deductionDates',
        width: 132,
        ellipsis: true,
        render: (text) => (text && String(text).trim() ? text : '—')
      },
      {
        title: '該期計入流水',
        dataIndex: 'lineGross',
        key: 'lineGross',
        width: 120,
        align: 'right',
        render: (v) =>
          new Intl.NumberFormat('zh-TW', {
            style: 'currency',
            currency: 'TWD',
            minimumFractionDigits: 0
          }).format(v || 0)
      },
      {
        title: '應付分潤 (30%)',
        dataIndex: 'lineCommission',
        key: 'lineCommission',
        width: 130,
        align: 'right',
        render: (v) =>
          new Intl.NumberFormat('zh-TW', {
            style: 'currency',
            currency: 'TWD',
            minimumFractionDigits: 0
          }).format(v || 0)
      }
    ],
    []
  )

  const handleViewDetail = (record) => {
    navigate(`/subscription/info?merchantOrderNo=${record.merchantOrderNo}`)
  }

  const handleLogout = () => {
    sessionStorage.removeItem('subscription_view_token')
    setToken(null)
    navigate('/subscription/login')
  }

  // 计算财务数据（支持按月/按年两种方式）
  const calculateFinance = (subscription, mode = 'monthly') => {
    const totalAmount = subscription.amount || 0
    const paymentType = subscription.paymentType || 'monthly'
    const periodTimes = subscription.periodConfig?.periodTimes || 1

    let subscriptionTotalAmount
    let calculatedPeriodTimes

    if (mode === 'yearly') {
      // 按年计算：月订阅按12个月，年订阅只计算1年
      if (paymentType === 'monthly') {
        calculatedPeriodTimes = 12
        subscriptionTotalAmount = totalAmount * 12
      } else {
        // 年订阅只计算1年，不计算5年
        calculatedPeriodTimes = 1
        subscriptionTotalAmount = totalAmount * 1
      }
    } else {
      // 按月计算：按实际期数计算
      calculatedPeriodTimes = periodTimes
      subscriptionTotalAmount = totalAmount * periodTimes
    }

    const newebpayFee = subscriptionTotalAmount * 0.03
    const qianjinCommission = subscriptionTotalAmount * 0.3
    const actualRevenue =
      subscriptionTotalAmount - newebpayFee - qianjinCommission

    return {
      subscriptionTotalAmount,
      newebpayFee,
      qianjinCommission,
      actualRevenue,
      singlePeriodAmount: totalAmount,
      periodTimes: calculatedPeriodTimes
    }
  }

  const columns = [
    {
      title: '客戶姓名',
      dataIndex: ['customer', 'name'],
      key: 'customerName',
      width: 120
    },
    {
      title: '方案',
      dataIndex: 'itemDesc',
      key: 'itemDesc',
      width: 150,
      ellipsis: true
    },
    {
      title: '單期金額',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (text) => formatCurrency(text)
    },
    {
      title: '總期數',
      dataIndex: ['periodConfig', 'periodTimes'],
      key: 'periodTimes',
      width: 80,
      align: 'center'
    },
    {
      title: '訂閱總金額',
      key: 'subscriptionTotalAmount',
      width: 140,
      align: 'right',
      render: (_, record) => {
        // 优先使用后端返回的财务数据，如果没有则使用前端计算
        const finance =
          record.finance?.[calculationMode] ||
          calculateFinance(record, calculationMode)
        return (
          <span style={{ fontWeight: 600, color: '#5a5a5a' }}>
            {formatCurrency(finance.subscriptionTotalAmount)}
          </span>
        )
      }
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status) => getStatusTag(status)
    },
    {
      title: '訂單號',
      dataIndex: 'merchantOrderNo',
      key: 'merchantOrderNo',
      width: 200,
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text) => formatDate(text)
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Button
          type='link'
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
          style={{ color: '#a8b5c0' }}
        >
          查看
        </Button>
      )
    }
  ]

  // 过滤数据（如果有搜索）
  const filteredSubscriptions = filters.search
    ? subscriptions.filter(
        (sub) =>
          sub.merchantOrderNo
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          sub.customer?.name
            ?.toLowerCase()
            .includes(filters.search.toLowerCase()) ||
          sub.itemDesc?.toLowerCase().includes(filters.search.toLowerCase())
      )
    : subscriptions

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Title>
            <DollarOutlined /> 訂閱訂單列表
          </Title>
          <ActionButtons>
            <StyledButton
              icon={<FileTextOutlined />}
              onClick={() => navigate('/subscription/applications')}
            >
              申請表單管理
            </StyledButton>
            <StyledButton
              icon={<ReloadOutlined />}
              onClick={fetchSubscriptions}
              loading={loading}
            >
              重新整理
            </StyledButton>
            <StyledButton
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              danger
            >
              登出
            </StyledButton>
          </ActionButtons>
        </Header>

        {summary?.settlement && (
          <SummaryCard
            title={
              <span>
                <DollarOutlined /> 應付分潤結算（截止月 {summary.settlement.month}
                ，{summary.settlement.timezone}）
              </span>
            }
          >
            {summary.settlement.periodStart && summary.settlement.periodEnd && (
              <p
                style={{
                  fontSize: 14,
                  color: '#5a5a5a',
                  margin: '0 0 12px',
                  fontWeight: 500
                }}
              >
                本期間：{summary.settlement.periodStart} ～{' '}
                {summary.settlement.periodEnd}
                <span style={{ fontWeight: 400, color: '#8a8a8a', marginLeft: 8 }}>
                  （上月 12 日—截止月 11 日）
                </span>
              </p>
            )}
            {summary.settlement.error && (
              <Hint
                tone='warning'
                title={summary.settlement.error}
                style={{ marginBottom: 16 }}
              />
            )}
            {summary.settlement.excludeYearlyApplied && (
              <Hint
                tone='info'
                title='已勾選「分潤計算排除年繳」：年繳委託不會納入本結算。若要看到年繳分潤請取消勾選後重新整理。'
                style={{ marginBottom: 16 }}
              />
            )}
            <p
              style={{
                fontSize: 13,
                color: '#8a8a8a',
                marginBottom: 16,
                lineHeight: 1.55
              }}
            >
              此區為<strong>全庫</strong>、與下方列表篩選／分頁無關；結算期以<strong>
                每月 11 日
              </strong>
              為界（例：2/12—3/11 為一期，選單之「截止月」為 3 月）。每月 12 日起可結算上期。
              月繳以有效訂閱為主並補該期無扣款紀錄之一期；暫停僅在有成功扣款之期計入；年繳勿同時勾選排除年繳。
              明細「扣款日（歸屬）」與後端歸期一致，方便與藍新比對。
            </p>
            <SummaryGrid>
              <SummaryItem>
                <SummaryLabel>納入筆數</SummaryLabel>
                <SummaryValue>
                  {summary.settlement.lineCount ?? 0}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>其中年繳筆數</SummaryLabel>
                <SummaryValue>
                  {summary.settlement.yearlyLineCount ?? 0}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>該結算期流水加總（計入基礎）</SummaryLabel>
                <SummaryValueLarge>
                  {formatCurrency(summary.settlement.totalGrossAmount || 0)}
                </SummaryValueLarge>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>應付分潤加總 (30%)</SummaryLabel>
                <SummaryValueLarge style={{ color: '#a8b5c0' }}>
                  {formatCurrency(
                    summary.settlement.totalQianjinCommission || 0
                  )}
                </SummaryValueLarge>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>手續費估算 (3%)</SummaryLabel>
                <SummaryValue style={{ color: '#9ca3af' }}>
                  {formatCurrency(summary.settlement.totalNewebpayFee || 0)}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>實收估算</SummaryLabel>
                <SummaryValue>
                  {formatCurrency(summary.settlement.totalActualRevenue || 0)}
                </SummaryValue>
              </SummaryItem>
            </SummaryGrid>
            {Array.isArray(summary.settlement.ruleNotes) &&
              summary.settlement.ruleNotes.length > 0 && (
                <ul
                  style={{
                    margin: '16px 0 0',
                    paddingLeft: 20,
                    fontSize: 12,
                    color: '#8a8a8a',
                    lineHeight: 1.6
                  }}
                >
                  {summary.settlement.ruleNotes.map((note, i) => (
                    <li key={i}>{note}</li>
                  ))}
                </ul>
              )}
            {summary.settlement.linesTruncated && (
              <p style={{ fontSize: 12, color: '#c4a574', marginTop: 8 }}>
                明細僅顯示前 300 筆，請以加總數字為準或匯出後端延伸。
              </p>
            )}
            {(summary.settlement.lines || []).length > 0 && (
              <Table
                style={{ marginTop: 16 }}
                size='small'
                columns={settlementColumns}
                dataSource={summary.settlement.lines.map((row, i) => ({
                  ...row,
                  key: row.merchantOrderNo + row.basis + i
                }))}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            )}
          </SummaryCard>
        )}

        {/* 財務匯總（本頁列表試算） */}
        {summary?.finance && (
          <SummaryCard
            title={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}
              >
                <div>
                  <span>
                    <DollarOutlined /> 財務匯總（本頁試算）
                  </span>
                  <div
                    style={{
                      fontSize: 12,
                      color: '#8a8a8a',
                      fontWeight: 400,
                      marginTop: 4
                    }}
                  >
                    「按月／按年」僅加總<strong>目前列表頁</strong>中啟用中之單期／年試算。
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}
                >
                  <Button
                    size='small'
                    type={calculationMode === 'monthly' ? 'primary' : 'default'}
                    onClick={() => setCalculationMode('monthly')}
                    style={{
                      borderRadius: '8px',
                      background:
                        calculationMode === 'monthly'
                          ? 'linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%)'
                          : '#f5f5f0',
                      border:
                        calculationMode === 'monthly'
                          ? 'none'
                          : '1px solid #d4c4b0',
                      color:
                        calculationMode === 'monthly' ? '#ffffff' : '#5a5a5a'
                    }}
                  >
                    按月計算
                  </Button>
                  <Button
                    size='small'
                    type={calculationMode === 'yearly' ? 'primary' : 'default'}
                    onClick={() => setCalculationMode('yearly')}
                    style={{
                      borderRadius: '8px',
                      background:
                        calculationMode === 'yearly'
                          ? 'linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%)'
                          : '#f5f5f0',
                      border:
                        calculationMode === 'yearly'
                          ? 'none'
                          : '1px solid #d4c4b0',
                      color:
                        calculationMode === 'yearly' ? '#ffffff' : '#5a5a5a'
                    }}
                  >
                    按年計算
                  </Button>
                  <Checkbox
                    checked={excludeYearlyFromCommission}
                    onChange={(e) =>
                      setExcludeYearlyFromCommission(e.target.checked)
                    }
                    style={{
                      marginLeft: '8px',
                      whiteSpace: 'nowrap',
                      color: '#5a5a5a'
                    }}
                  >
                    分潤計算排除年繳
                  </Checkbox>
                </div>
              </div>
            }
          >
            <SummaryGrid>
              <SummaryItem>
                <SummaryLabel>總訂閱數</SummaryLabel>
                <SummaryValue>{summary.totalSubscriptions || 0}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>啟用中</SummaryLabel>
                <SummaryValue>{summary.activeSubscriptions || 0}</SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>
                  總訂閱金額
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#8a8a8a',
                      marginLeft: '4px',
                      display: 'block',
                      fontWeight: 300
                    }}
                  >
                    (僅計算啟用中)
                  </span>
                </SummaryLabel>
                <SummaryValueLarge>
                  {formatCurrency(
                    summary.finance[calculationMode]?.totalSubscriptionAmount ||
                      0
                  )}
                </SummaryValueLarge>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>總手續費 (3%)</SummaryLabel>
                <SummaryValue style={{ color: '#9ca3af' }}>
                  {formatCurrency(
                    summary.finance[calculationMode]?.totalNewebpayFee || 0
                  )}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>總分潤金 (30%)</SummaryLabel>
                <SummaryValue style={{ color: '#a8b5c0' }}>
                  {formatCurrency(
                    summary.finance[calculationMode]?.totalQianjinCommission ||
                      0
                  )}
                </SummaryValue>
              </SummaryItem>
              <SummaryItem>
                <SummaryLabel>總實際收入</SummaryLabel>
                <SummaryValueLarge>
                  {formatCurrency(
                    summary.finance[calculationMode]?.totalActualRevenue || 0
                  )}
                </SummaryValueLarge>
              </SummaryItem>
            </SummaryGrid>
          </SummaryCard>
        )}

        {/* 篩選器 */}
        <FilterCard>
          <FilterRow>
            <FilterItem>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                搜尋
              </label>
              <Input
                placeholder='搜尋訂單號、客戶姓名或方案'
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                style={{ borderRadius: '12px', borderColor: '#d4c4b0' }}
              />
            </FilterItem>
            <FilterItem>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                結算期（截止月）
              </label>
              <DatePicker
                picker='month'
                allowClear={false}
                value={
                  settlementMonth
                    ? dayjs(settlementMonth, 'YYYY-MM')
                    : dayjs(defaultSettlementClosingYm(), 'YYYY-MM')
                }
                onChange={(d) => {
                  setSettlementMonth(
                    d ? d.format('YYYY-MM') : defaultSettlementClosingYm()
                  )
                  setPagination((prev) => ({ ...prev, current: 1 }))
                }}
                format='YYYY-MM'
                style={{ width: '100%', borderRadius: '12px' }}
              />
            </FilterItem>
            <FilterItem>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                狀態
              </label>
              <Select
                placeholder='預設不含未付款'
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
                style={{ width: '100%', borderRadius: '12px' }}
              >
                <Select.Option value='active'>啟用中</Select.Option>
                <Select.Option value='pending'>未付款</Select.Option>
                <Select.Option value='paused'>已暫停</Select.Option>
                <Select.Option value='expired'>已過期</Select.Option>
              </Select>
              <Tooltip
                title={
                  filters.status
                    ? '已選定狀態時，請以狀態下拉為準（選「未付款」即可只看 pending）'
                    : '未勾選時列表預設隱藏未付款；勾選後與其他狀態一併列出'
                }
              >
                <div>
                  <Checkbox
                    checked={includePendingInList}
                    disabled={!!filters.status}
                    onChange={(e) => {
                      setIncludePendingInList(e.target.checked)
                      setPagination((prev) => ({ ...prev, current: 1 }))
                    }}
                    style={{
                      marginTop: 10,
                      color: '#5a5a5a',
                      whiteSpace: 'normal',
                      lineHeight: 1.4
                    }}
                  >
                    顯示未付款（pending）
                  </Checkbox>
                </div>
              </Tooltip>
            </FilterItem>
            <FilterItem>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                日期範圍
              </label>
              <RangePicker
                style={{ width: '100%', borderRadius: '12px' }}
                onChange={handleDateRangeChange}
                format='YYYY-MM-DD'
              />
            </FilterItem>
            <FilterItem>
              <Button
                onClick={handleResetFilters}
                style={{ width: '100%', borderRadius: '12px' }}
              >
                重置篩選
              </Button>
            </FilterItem>
          </FilterRow>
        </FilterCard>

        {/* 訂單列表 */}
        {loading && subscriptions.length === 0 ? (
          <LoadingContainer>
            <Spin size='large' />
          </LoadingContainer>
        ) : (
          <StyledTable
            columns={columns}
            dataSource={filteredSubscriptions}
            rowKey='_id'
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆記錄`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
            onRow={(record) => ({
              onClick: () => handleViewDetail(record),
              style: { cursor: 'pointer' }
            })}
          />
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default SubscriptionListPage
